import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Sheet from '@mui/joy/Sheet';
import Textarea from '@mui/joy/Textarea';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import {
    Send,
    AttachFile,
    Close,
    CheckCircle,
    Refresh,
    Email,
    Computer,
    Download,
    Image as ImageIcon,
    InsertDriveFile,
    StickyNote2,
    SupportAgent,
} from '@mui/icons-material';
import { TicketItem } from './SupportTickets';
import {
    GetTicketMessages,
    PostTicketMessage,
    GetTicketDetail,
    UpdateTicket,
    CloseTicket,
    ReopenTicket,
    GetSuperAdminsList,
} from '../../../axios/Axios';
import { useUser } from '../../dashboard-components/UserContext';

interface MessageAttachment {
    id: number;
    file_name: string;
    file_size: number;
    content_type: string;
    url: string;
}

interface TicketMessageItem {
    id: number;
    ticket: number;
    sender: number | null;
    sender_name: string;
    sender_is_super_admin: boolean;
    sender_email: string;
    body: string;
    html_body: string;
    source: 'crm' | 'email';
    is_internal_note: boolean;
    created_at: string;
    attachments: MessageAttachment[];
}

interface TicketDetail {
    id: number;
    ticket_number: string;
    subject: string;
    category: string;
    reason: string;
    priority: string;
    status: string;
    franchise: number;
    franchise_name: string;
    created_by: number;
    created_by_detail: { id: number; username: string; full_name: string };
    assigned_to: number | null;
    assigned_to_detail: { id: number; username: string; full_name: string } | null;
    created_at: string;
    updated_at: string;
    closed_at: string | null;
}

interface TicketChatPaneProps {
    ticket: TicketItem | null;
    isSuperAdmin: boolean;
    onTicketUpdated: () => void;
    refreshList: () => void;
}

const STATUS_COLORS: Record<string, 'primary' | 'warning' | 'success' | 'neutral' | 'danger'> = {
    'Open': 'primary',
    'In Progress': 'warning',
    'Awaiting Reply': 'neutral',
    'Resolved': 'success',
    'Closed': 'neutral',
};

function formatDate(dateStr: string) {
    const d = new Date(dateStr);
    return d.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

function isImageType(contentType: string): boolean {
    return contentType.startsWith('image/');
}

export default function TicketChatPane({ ticket, isSuperAdmin, onTicketUpdated, refreshList }: TicketChatPaneProps) {
    const { user } = useUser();
    const [ticketDetail, setTicketDetail] = useState<TicketDetail | null>(null);
    const [messages, setMessages] = useState<TicketMessageItem[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const [messageBody, setMessageBody] = useState('');
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const [superAdmins, setSuperAdmins] = useState<{ id: number; username: string; full_name: string }[]>([]);
    const [loadedTicketId, setLoadedTicketId] = useState<number | null>(null);

    useEffect(() => {
        if (isSuperAdmin) {
            GetSuperAdminsList().then(res => setSuperAdmins(res.data)).catch(() => {});
        }
    }, [isSuperAdmin]);

    const fetchMessages = useCallback(async (ticketId: number, pageNum: number) => {
        if (pageNum === 1) setIsLoadingMessages(true);
        else setIsLoadingMore(true);

        try {
            const res = await GetTicketMessages(ticketId, pageNum);
            const { messages: newMsgs, hasMore: more } = res.data;
            setMessages(prev => pageNum === 1 ? newMsgs : [...newMsgs, ...prev]);
            setHasMore(more);
        } catch (err) {
            console.error('Failed to fetch messages:', err);
        } finally {
            setIsLoadingMessages(false);
            setIsLoadingMore(false);
        }
    }, []);

    const fetchDetail = useCallback(async (ticketId: number) => {
        try {
            const res = await GetTicketDetail(ticketId);
            setTicketDetail(res.data);
        } catch (err) {
            console.error('Failed to fetch ticket detail:', err);
        }
    }, []);

    useEffect(() => {
        if (!ticket) {
            setMessages([]);
            setTicketDetail(null);
            setLoadedTicketId(null);
            return;
        }
        if (loadedTicketId === ticket.id) return;
        setMessages([]);
        setPage(1);
        setHasMore(false);
        setMessageBody('');
        setIsInternalNote(false);
        setAttachedFiles([]);
        setLoadedTicketId(ticket.id);
        fetchDetail(ticket.id);
        fetchMessages(ticket.id, 1);
    }, [ticket?.id]);

    useEffect(() => {
        if (messages.length > 0 && page === 1) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, page]);

    const handleLoadMore = () => {
        if (!ticket || isLoadingMore) return;
        const nextPage = page + 1;
        setPage(nextPage);
        fetchMessages(ticket.id, nextPage);
    };

    const handleSend = async () => {
        if (!ticket || (!messageBody.trim() && attachedFiles.length === 0)) return;

        setIsSending(true);
        try {
            const formData = new FormData();
            formData.append('body', messageBody);
            if (isInternalNote) formData.append('is_internal_note', 'true');
            attachedFiles.forEach(f => formData.append('files', f));

            const res = await PostTicketMessage(ticket.id, formData);
            setMessages(prev => [...prev, res.data]);
            setMessageBody('');
            setAttachedFiles([]);
            setIsInternalNote(false);
            refreshList();

            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        } catch (err) {
            console.error('Failed to send message:', err);
        } finally {
            setIsSending(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setAttachedFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index: number) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleStatusChange = async (newStatus: string) => {
        if (!ticket) return;
        try {
            await UpdateTicket(ticket.id, { status: newStatus });
            fetchDetail(ticket.id);
            onTicketUpdated();
        } catch (err) {
            console.error('Failed to update status:', err);
        }
    };

    const handleAssign = async (userId: string | null) => {
        if (!ticket) return;
        try {
            await UpdateTicket(ticket.id, { assigned_to: userId || null });
            fetchDetail(ticket.id);
            onTicketUpdated();
        } catch (err) {
            console.error('Failed to assign ticket:', err);
        }
    };

    const handleClose = async () => {
        if (!ticket) return;
        try {
            await CloseTicket(ticket.id);
            fetchDetail(ticket.id);
            onTicketUpdated();
        } catch (err) {
            console.error('Failed to close ticket:', err);
        }
    };

    const handleReopen = async () => {
        if (!ticket) return;
        try {
            await ReopenTicket(ticket.id);
            fetchDetail(ticket.id);
            onTicketUpdated();
        } catch (err) {
            console.error('Failed to reopen ticket:', err);
        }
    };

    if (!ticket) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4, bgcolor: 'background.level1' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <SupportAgent sx={{ fontSize: 48, color: 'neutral.300', mb: 1 }} />
                    <Typography level="title-lg" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                        Select a ticket
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        Choose a ticket from the list to view the conversation
                    </Typography>
                </Box>
            </Box>
        );
    }

    const detail = ticketDetail;
    const isResolved = detail?.status === 'Resolved' || detail?.status === 'Closed';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Ticket header */}
            <Sheet
                sx={{
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                {/* Top row: chips + resolve/reopen */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary' }}>
                        {detail?.ticket_number || ticket.ticket_number}
                    </Typography>
                    <Chip
                        size="sm"
                        variant="soft"
                        color={STATUS_COLORS[detail?.status || ticket.status] || 'neutral'}
                    >
                        {detail?.status || ticket.status}
                    </Chip>
                    <Chip size="sm" variant="outlined" color="neutral">
                        {detail?.category || ticket.category}
                    </Chip>
                    <Chip size="sm" variant="outlined" color="neutral">
                        {detail?.priority || ticket.priority}
                    </Chip>
                    <Box sx={{ ml: 'auto' }}>
                        {isResolved ? (
                            <Button size="sm" variant="outlined" color="warning" startDecorator={<Refresh />} onClick={handleReopen}>
                                Reopen
                            </Button>
                        ) : (
                            <Button size="sm" variant="soft" color="success" startDecorator={<CheckCircle />} onClick={handleClose}>
                                Resolve
                            </Button>
                        )}
                    </Box>
                </Box>

                <Typography level="title-lg" sx={{ fontWeight: 700, mb: 0.25 }}>
                    {detail?.subject || ticket.subject}
                </Typography>

                {isSuperAdmin && (
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {detail?.franchise_name || ticket.franchise_name} &middot; Created by {detail?.created_by_detail?.full_name || ticket.created_by_name}
                    </Typography>
                )}

                {/* Super admin controls */}
                {isSuperAdmin && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                        <Select
                            size="sm"
                            placeholder="Assign to..."
                            value={detail?.assigned_to?.toString() || ''}
                            onChange={(_, val) => handleAssign(val)}
                            sx={{ minWidth: 150 }}
                        >
                            <Option value="">Unassigned</Option>
                            {superAdmins.map(admin => (
                                <Option key={admin.id} value={admin.id.toString()}>
                                    {admin.full_name}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            size="sm"
                            value={detail?.status || ticket.status}
                            onChange={(_, val) => val && handleStatusChange(val)}
                            sx={{ minWidth: 140 }}
                        >
                            <Option value="Open">Open</Option>
                            <Option value="In Progress">In Progress</Option>
                            <Option value="Awaiting Reply">Awaiting Reply</Option>
                            <Option value="Resolved">Resolved</Option>
                            <Option value="Closed">Closed</Option>
                        </Select>
                    </Box>
                )}
            </Sheet>

            {/* Messages area */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: { xs: 1.5, sm: 3 },
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    bgcolor: 'background.level1',
                }}
            >
                {hasMore && (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Button
                            size="sm"
                            variant="plain"
                            loading={isLoadingMore}
                            onClick={handleLoadMore}
                        >
                            Load older messages
                        </Button>
                    </Box>
                )}

                {isLoadingMessages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : messages.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography level="body-md" sx={{ color: 'text.tertiary' }}>
                            No messages yet.
                        </Typography>
                    </Box>
                ) : (
                    messages.map((msg) => (
                        <MessageBubble
                            key={msg.id}
                            message={msg}
                            isOwnMessage={msg.sender === user?.pk}
                            isSuperAdmin={isSuperAdmin}
                        />
                    ))
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Composer */}
            {!isResolved && (
                <Sheet
                    sx={{
                        px: { xs: 1.5, sm: 2.5 },
                        py: 1.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                    }}
                >
                    {attachedFiles.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {attachedFiles.map((f, i) => (
                                <Chip
                                    key={i}
                                    size="sm"
                                    variant="soft"
                                    endDecorator={
                                        <IconButton size="sm" variant="plain" onClick={() => removeFile(i)}>
                                            <Close sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    }
                                >
                                    {f.name} ({formatFileSize(f.size)})
                                </Chip>
                            ))}
                        </Box>
                    )}

                    {isInternalNote && (
                        <Chip size="sm" color="warning" variant="soft" sx={{ mb: 1 }}>
                            Internal note - only visible to super admins
                        </Chip>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <Textarea
                            placeholder={isInternalNote ? 'Write an internal note...' : 'Type your reply...'}
                            value={messageBody}
                            onChange={(e) => setMessageBody(e.target.value)}
                            minRows={1}
                            maxRows={6}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            sx={{
                                flex: 1,
                                ...(isInternalNote && {
                                    bgcolor: 'warning.softBg',
                                    borderColor: 'warning.outlinedBorder',
                                }),
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                multiple
                                hidden
                            />
                            <Tooltip title="Attach files">
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <AttachFile />
                                </IconButton>
                            </Tooltip>
                            {isSuperAdmin && (
                                <Tooltip title={isInternalNote ? 'Switch to reply' : 'Switch to internal note'}>
                                    <IconButton
                                        size="sm"
                                        variant={isInternalNote ? 'soft' : 'plain'}
                                        color={isInternalNote ? 'warning' : 'neutral'}
                                        onClick={() => setIsInternalNote(!isInternalNote)}
                                    >
                                        <StickyNote2 />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Button
                                size="sm"
                                loading={isSending}
                                disabled={!messageBody.trim() && attachedFiles.length === 0}
                                onClick={handleSend}
                                endDecorator={<Send />}
                            >
                                Send
                            </Button>
                        </Box>
                    </Box>
                </Sheet>
            )}

            {isResolved && (
                <Sheet
                    sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        textAlign: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        This ticket is resolved. <Button size="sm" variant="plain" onClick={handleReopen}>Reopen</Button> to continue the conversation.
                    </Typography>
                </Sheet>
            )}
        </Box>
    );
}


function MessageBubble({
    message,
    isOwnMessage,
    isSuperAdmin,
}: {
    message: TicketMessageItem;
    isOwnMessage: boolean;
    isSuperAdmin: boolean;
}) {
    if (message.is_internal_note) {
        return (
            <Box
                sx={{
                    alignSelf: 'center',
                    maxWidth: { xs: '95%', sm: '80%' },
                    width: '100%',
                    px: 2,
                    py: 1.5,
                    bgcolor: 'warning.50',
                    border: '1px dashed',
                    borderColor: 'warning.300',
                    borderRadius: 'md',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <StickyNote2 sx={{ fontSize: 14, color: 'warning.600' }} />
                    <Typography level="body-xs" sx={{ fontWeight: 700, color: 'warning.700' }}>
                        Internal Note
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', ml: 'auto' }}>
                        {message.sender_name} &middot; {formatDate(message.created_at)}
                    </Typography>
                </Box>
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', color: 'warning.800' }}>
                    {message.body}
                </Typography>
            </Box>
        );
    }

    const isAdmin = message.sender_is_super_admin;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                maxWidth: { xs: '92%', sm: '75%' },
                alignSelf: isOwnMessage ? 'flex-end' : 'flex-start',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, px: 0.5 }}>
                <Typography level="body-xs" sx={{ fontWeight: 600, color: isAdmin ? 'primary.600' : 'text.secondary' }}>
                    {message.sender_name}
                    {isAdmin && ' (Yeti HQ)'}
                </Typography>
                <Tooltip title={message.source === 'email' ? 'Sent via email' : 'Sent from CRM'}>
                    {message.source === 'email' ? (
                        <Email sx={{ fontSize: 13, color: 'text.tertiary' }} />
                    ) : (
                        <Computer sx={{ fontSize: 13, color: 'text.tertiary' }} />
                    )}
                </Tooltip>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {formatDate(message.created_at)}
                </Typography>
            </Box>

            <Sheet
                variant={isOwnMessage ? 'solid' : 'outlined'}
                color={isOwnMessage ? 'primary' : 'neutral'}
                sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 'lg',
                    borderTopRightRadius: isOwnMessage ? 0 : undefined,
                    borderTopLeftRadius: isOwnMessage ? undefined : 0,
                    boxShadow: 'xs',
                    minWidth: 60,
                    ...(!isOwnMessage && { bgcolor: 'background.surface' }),
                }}
            >
                <Typography
                    level="body-md"
                    sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: isOwnMessage ? 'common.white' : 'text.primary',
                        lineHeight: 1.6,
                    }}
                >
                    {message.body}
                </Typography>
            </Sheet>

            {message.attachments && message.attachments.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, px: 0.5 }}>
                    {message.attachments.map((att) => (
                        <Chip
                            key={att.id}
                            size="sm"
                            variant="outlined"
                            component="a"
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startDecorator={
                                isImageType(att.content_type) ? (
                                    <ImageIcon sx={{ fontSize: 14 }} />
                                ) : (
                                    <InsertDriveFile sx={{ fontSize: 14 }} />
                                )
                            }
                            endDecorator={<Download sx={{ fontSize: 12 }} />}
                            sx={{ cursor: 'pointer', textDecoration: 'none' }}
                        >
                            {att.file_name} ({formatFileSize(att.file_size)})
                        </Chip>
                    ))}
                </Box>
            )}
        </Box>
    );
}
